import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, ButtonGroup, Typography } from '@mui/material';
import copyText from '../../utils/copyText';
import { detectImage, imageLabel } from '../../utils/imageValues';
import ImageThumbnail from './ImageThumbnail';

const PAGE_SIZE = 100;
const STRING_LIMIT = 200;
const RAW_DISPLAY_LIMIT = 20000;
const INDENT = 16;

const pretty = (value) => {
  if (value === undefined) return '';
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
};

const formatSize = (chars) => (chars < 1024 ? `${chars} chars` : `${Math.round(chars / 1024)} KB`);

// Colour roles, resolved from the MUI theme so they read well in light and dark mode.
const pick = (name) => (theme) => {
  const c = theme.palette[name];
  return theme.palette.mode === 'dark' ? c.light : c.dark;
};
const rootSx = {
  fontFamily: 'monospace',
  fontSize: '0.8125rem',
  lineHeight: 1.6,
  '& .json-key': { color: pick('primary') },
  '& .json-string': { color: pick('success') },
  '& .json-number': { color: pick('warning') },
  '& .json-boolean': { color: pick('secondary') },
  '& .json-null': { color: pick('error') },
  '& .json-punct': { color: 'text.secondary' },
  '& .json-summary': { color: 'text.secondary', fontStyle: 'italic' },
};
const toggleSx = {
  all: 'unset',
  cursor: 'pointer',
  display: 'inline-block',
  width: INDENT,
  textAlign: 'center',
  color: 'text.secondary',
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
};
const linkBtnSx = {
  all: 'unset',
  cursor: 'pointer',
  color: 'primary.main',
  textDecoration: 'underline',
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
};
const breakSx = { whiteSpace: 'pre-wrap', wordBreak: 'break-all' };

// How a freshly mounted node decides if it starts open; changed by Expand all / Collapse all.
const initialOpen = (mode, depth, defaultDepth) => {
  if (mode === 'all') return true;
  if (mode === 'collapsed') return depth < 1;
  return depth < defaultDepth;
};

const Punct = ({ children }) => <span className="json-punct">{children}</span>;

const StringValue = ({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const long = value.length > STRING_LIMIT;
  const shown = long && !expanded ? value.slice(0, STRING_LIMIT) : value;
  let quoted = JSON.stringify(shown);
  if (long && !expanded) quoted = quoted.slice(0, -1); // reopen the closing quote after the ellipsis
  return (
    <>
      <span className="json-string" style={breakSx}>{quoted}</span>
      {long && !expanded && <span className="json-summary">… ({value.length} chars)</span>}
      {long && !expanded && <span className="json-string">&quot;</span>}
      {long && (
        <>
          {' '}
          <Box component="button" type="button" sx={linkBtnSx} aria-expanded={expanded}
            onClick={() => setExpanded((e) => !e)}>
            {expanded ? 'hide' : 'show'}
          </Box>
        </>
      )}
    </>
  );
};

// An embedded image replaces its base64 text with a size label (the thumbnail follows the comma).
const ImageValue = ({ image }) => <span className="json-summary">{imageLabel(image)}</span>;

const Primitive = ({ value, image }) => {
  if (value === null) return <span className="json-null">null</span>;
  if (image) return <ImageValue image={image} />;
  if (typeof value === 'string') return <StringValue value={value} />;
  if (typeof value === 'number') return <span className="json-number">{String(value)}</span>;
  if (typeof value === 'boolean') return <span className="json-boolean">{String(value)}</span>;
  return <span className="json-null">{String(value)}</span>;
};

const Node = ({ name, index, value, depth, last, mode, defaultDepth, siblings }) => {
  const isArray = Array.isArray(value);
  const isContainer = value !== null && typeof value === 'object';
  const [open, setOpen] = useState(() => initialOpen(mode, depth, defaultDepth));
  const [visible, setVisible] = useState(PAGE_SIZE);

  // Only the visible page is materialised, so opening a huge array/object stays cheap.
  const keys = useMemo(() => (isContainer && open && !isArray ? Object.keys(value) : null), [isContainer, isArray, open, value]);
  const total = !isContainer || !open ? 0 : isArray ? value.length : keys.length;
  const shownEntries = useMemo(() => {
    if (!total) return [];
    const n = Math.min(visible, total);
    if (isArray) return Array.from({ length: n }, (_, i) => [i, value[i]]);
    return keys.slice(0, n).map((k) => [k, value[k]]);
  }, [total, visible, isArray, keys, value]);

  const image = useMemo(
    () => (isContainer ? null : detectImage(name, value, siblings)),
    [isContainer, name, value, siblings],
  );

  const label = typeof name === 'string'
    ? <><span className="json-key">{JSON.stringify(name)}</span><Punct>: </Punct></>
    : null;
  const comma = last ? null : <Punct>,</Punct>;

  if (!isContainer) {
    return (
      <div>
        <span style={{ display: 'inline-block', width: INDENT }} />
        {label}
        <Primitive value={value} image={image} />
        {comma}
        {image && (
          <Box sx={{ pl: `${INDENT}px` }}>
            <ImageThumbnail image={image} alt="Embedded image preview" />
          </Box>
        )}
      </div>
    );
  }

  const count = isArray ? value.length : Object.keys(value).length;
  const [openCh, closeCh] = isArray ? ['[', ']'] : ['{', '}'];
  const keyText = typeof name === 'string' ? name : index === undefined ? 'root' : `item ${index}`;
  const summary = `${isArray ? '[…]' : '{…}'} ${count} ${isArray ? (count === 1 ? 'item' : 'items') : (count === 1 ? 'key' : 'keys')}`;
  const remaining = total - shownEntries.length;

  return (
    <div>
      <Box component="button" type="button" sx={toggleSx} aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} ${keyText}`}
        onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'}
      </Box>
      {label}
      {count === 0 ? (
        <Punct>{openCh}{closeCh}</Punct>
      ) : open ? (
        <Punct>{openCh}</Punct>
      ) : (
        <span className="json-summary">{summary}</span>
      )}
      {open && count > 0 ? null : comma}
      {open && count > 0 && (
        <>
          <Box sx={{ pl: `${INDENT}px` }}>
            {shownEntries.map(([k, v], i) => (
              <Node key={k} name={isArray ? null : k} index={isArray ? k : undefined} value={v} depth={depth + 1}
                last={i === total - 1} mode={mode} defaultDepth={defaultDepth}
                siblings={isArray ? null : value} />
            ))}
            {remaining > 0 && (
              <Box sx={{ pl: `${INDENT}px` }}>
                <Button size="small" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                  {`Show ${Math.min(PAGE_SIZE, remaining)} more (${remaining} remaining)`}
                </Button>
              </Box>
            )}
          </Box>
          <span style={{ display: 'inline-block', width: INDENT }} />
          <Punct>{closeCh}</Punct>{comma}
        </>
      )}
    </div>
  );
};

const TOKEN_RE = /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false)\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;

// Colours already-pretty-printed JSON text (bounded by RAW_DISPLAY_LIMIT, so a regex pass is cheap).
const highlight = (text) => {
  const out = [];
  let last = 0;
  let m;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    let cls = 'json-number';
    if (m[1] !== undefined) cls = m[2] ? 'json-key' : 'json-string';
    else if (m[3] !== undefined) cls = 'json-boolean';
    else if (m[0] === 'null') cls = 'json-null';
    if (m[2]) {
      out.push(<span key={m.index} className={cls}>{m[1]}</span>);
      out.push(<span key={`${m.index}c`} className="json-punct">{m[2]}</span>);
    } else {
      out.push(<span key={m.index} className={cls}>{m[0]}</span>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const RawView = ({ value }) => {
  const [showAll, setShowAll] = useState(false);
  const text = useMemo(() => pretty(value), [value]);
  const tooLong = text.length > RAW_DISPLAY_LIMIT;
  const shown = tooLong && !showAll ? text.slice(0, RAW_DISPLAY_LIMIT) : text;
  const body = useMemo(() => highlight(shown), [shown]);
  return (
    <>
      <Box component="pre" sx={{ m: 0, font: 'inherit', ...breakSx }}>
        {body}
        {tooLong && !showAll ? '\n…' : ''}
      </Box>
      {tooLong && (
        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
          <Typography variant="caption" color="text.secondary">
            {showAll ? `Showing all ${formatSize(text.length)}` : `Showing first ${formatSize(RAW_DISPLAY_LIMIT)} of ${formatSize(text.length)}`}
          </Typography>
          <Button size="small" onClick={() => setShowAll((s) => !s)}>
            {showAll ? 'Show less' : 'Show all'}
          </Button>
        </Box>
      )}
    </>
  );
};

const JsonViewer = ({ value, maxHeight = 400, defaultExpandDepth = 2 }) => {
  const [mode, setMode] = useState('default');
  const [generation, setGeneration] = useState(0);
  const [raw, setRaw] = useState(false);
  const [copyState, setCopyState] = useState('Copy');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const applyMode = (next) => { setMode(next); setGeneration((g) => g + 1); };

  const handleCopy = useCallback(async () => {
    let label = 'Copied';
    try { await copyText(pretty(value)); } catch { label = 'Copy failed'; }
    setCopyState(label);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopyState('Copy'), 1500);
  }, [value]);

  return (
    <Box>
      <ButtonGroup size="small" variant="text" sx={{ mb: 0.5 }} aria-label="JSON viewer actions">
        <Button onClick={() => applyMode('all')} disabled={raw}>Expand all</Button>
        <Button onClick={() => applyMode('collapsed')} disabled={raw}>Collapse all</Button>
        <Button onClick={() => setRaw((r) => !r)} aria-pressed={raw}>Raw</Button>
        <Button onClick={handleCopy}>{copyState}</Button>
      </ButtonGroup>
      <Box sx={{ ...rootSx, p: 1.5, maxHeight, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1 }}>
        {raw ? <RawView value={value} /> : (
          <Node key={generation} name={null} value={value} depth={0} last mode={mode}
            defaultDepth={defaultExpandDepth} siblings={null} />
        )}
      </Box>
    </Box>
  );
};

export default JsonViewer;
